import { httpClient } from '@/lib/api/client';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { env } from '@/lib/config/env';
import { STORAGE_KEYS } from '@/lib/config/constants';
import { authStorage } from '@/lib/utils/authStorage';
import { mockApi } from '@/lib/api/mock/adapter';
import type { User } from '@/types/domain';
import type { LoginFormValues } from './schemas';

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: User;

  paymentMethods?: string[];

  features?: string[];

  tenantName?: string;
}

export interface UpdateProfileInput {
  fullName: string;
  avatarUrl?: string;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export interface SessionResponse {
  user: User;
  paymentMethods?: string[];
  features?: string[];
  tenantName?: string;
}

export interface AuthRepository {
  login(credentials: LoginFormValues): Promise<AuthResponse>;
  logout(): Promise<void>;
  me(): Promise<User>;

  session(): Promise<SessionResponse>;
  updateProfile(data: UpdateProfileInput): Promise<User>;
  changePassword(data: ChangePasswordInput): Promise<{ message: string }>;
  forgotPassword(email: string): Promise<{ message: string }>;
  resetPassword(token: string, password: string): Promise<{ message: string }>;
}

const http: AuthRepository = {
  login: (credentials) =>
    httpClient.post<AuthResponse>(ENDPOINTS.auth.login, { ...credentials, client: 'admin' } as LoginFormValues & { client: string }),

  logout: async () => {
    try {
      await httpClient.post(ENDPOINTS.auth.logout);
    } finally {
      authStorage.remove(STORAGE_KEYS.authToken);
      authStorage.remove(STORAGE_KEYS.refreshToken);
      authStorage.remove(STORAGE_KEYS.authUser);
    }
  },

  me: () => httpClient.get<User>(ENDPOINTS.auth.me),

  session: () => httpClient.get<SessionResponse>(ENDPOINTS.auth.me),

  updateProfile: (data) => httpClient.put<User>(ENDPOINTS.auth.updateProfile, data),

  changePassword: (data) =>
    httpClient.post<{ message: string }>(ENDPOINTS.auth.changePassword, data),

  forgotPassword: (email) =>
    httpClient.post<{ message: string }, { email: string }>(ENDPOINTS.auth.forgotPassword, {
      email,
    }),

  resetPassword: (token, password) =>
    httpClient.post<{ message: string }, { token: string; password: string }>(
      ENDPOINTS.auth.resetPassword,
      { token, password },
    ),
};

const mock: AuthRepository = {
  login: (credentials) => mockApi.login(credentials.email, credentials.password),
  logout: async () => {},
  me: () => mockApi.me(),
  session: async () => ({
    user: await mockApi.me(),
    paymentMethods: [],
    features: [],
    tenantName: undefined,
  }),
  updateProfile: async (data) => ({ ...(await mockApi.me()), ...data }),
  changePassword: async () => ({ message: 'Password updated' }),
  forgotPassword: async () => ({
    message: 'If that email is registered, a reset link has been sent.',
  }),
  resetPassword: async () => ({ message: 'Password updated. You can now sign in.' }),
};

export const authRepository: AuthRepository = env.useMockApi ? mock : http;
